import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { makeT } from "../lib/i18n";
import { Loader2 } from "lucide-react";

import Centered from "./micro_research/Centered";
import ScreenConsent from "./micro_research/ScreenConsent";
import ScreenScreener from "./micro_research/ScreenScreener";
import ScreenPreflight from "./micro_research/ScreenPreflight";
import ScreenMc from "./micro_research/ScreenMc";
import ScreenQual from "./micro_research/ScreenQual";
import ScreenFinal from "./micro_research/ScreenFinal";

// ============ IndexedDB minimal helper ============
const DB_NAME = "rl_audio_buffer";
const STORE = "chunks";
function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { autoIncrement: true });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function saveChunkLocal(blob, meta) {
  const db = await openDb();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).add({ blob, meta, ts: Date.now() });
    tx.oncomplete = () => resolve();
  });
}

/**
 * MicroResearch — voice-first conversational interview controller.
 *
 * This file is intentionally state-heavy and layout-light: every phase
 * screen lives in `./micro_research/Screen*.jsx`. The orchestrator's job
 * is to (a) hold the session state, (b) own the MediaRecorder lifecycle,
 * (c) talk to the backend, and (d) route the active phase to the right
 * screen component.
 */
export default function MicroResearch() {
  const [search] = useSearchParams();
  const nav = useNavigate();
  const campaignId = search.get("campaignId");
  const source = search.get("source") || "intercept";
  // Programmatic Panel Network URL-token params (PureSpectrum transaction_id / respondent_id).
  // PS routes participants here with our canonical params (participantId /
  // assignmentId / projectId) via `[%ps_psid%]` / `[%ps_transaction_id%]` /
  // `[%ps_survey_id%]` macros on the buyer live_url. PS ALSO auto-appends
  // `&transaction_id=<real>` regardless of the macro (documented in PS's
  // Buyer Redirect Guide), so accepting `transaction_id` as a fallback
  // alias guarantees we still capture the id when the buyer's Variable
  // Mapping settings suppress the custom-named param. Same defensive
  // aliasing for PSID + survey_id.
  //
  // `psNorm` strips PS's un-substituted literal macro placeholders (e.g.
  // `[transaction_id]`, `[PSID]`, `[%ps_transaction_id%]` if the buyer
  // config didn't wire the macro). Un-substituted tokens arrive verbatim
  // as `?assignmentId=[transaction_id]` which is a truthy string but a
  // useless value; treat them as absent so the alias fallback picks up
  // PS's auto-appended real `transaction_id` param.
  const psNorm = (v) => {
    if (!v) return null;
    const t = v.trim();
    if (!t) return null;
    if (/^\[.*\]$/.test(t) || /^\[%.*%\]$/.test(t)) return null;
    return t;
  };
  const participantId = psNorm(search.get("participantId")) || psNorm(search.get("participant_id")) || psNorm(search.get("PSID")) || psNorm(search.get("psid"));
  const assignmentId = psNorm(search.get("assignmentId")) || psNorm(search.get("assignment_id")) || psNorm(search.get("transaction_id")) || psNorm(search.get("tid"));
  const projectId = psNorm(search.get("projectId")) || psNorm(search.get("project_id")) || psNorm(search.get("survey_id")) || psNorm(search.get("SID")) || psNorm(search.get("sid"));
  const pid = search.get("pid");

  const [phase, setPhase] = useState("init"); // init|consent|screener|preflight|mc|qual|finished|filled|terminated|review|denied|screened_out|already_participated
  const [error, setError] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [interviewId, setInterviewId] = useState(null);
  const [hideReward, setHideReward] = useState(false);

  const [screenerAnswers, setScreenerAnswers] = useState({});
  const [mcAnswers, setMcAnswers] = useState({});
  const [mcIndex, setMcIndex] = useState(0);

  const [qIndex, setQIndex] = useState(0);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [hasMic, setHasMic] = useState(null);
  const [finalScreen, setFinalScreen] = useState(null);

  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const videoPreviewRef = useRef(null);
  // Mount-init sentinel — see the /session/init useEffect below for why.
  const initFiredRef = useRef(false);

  const lang = campaign?.language === "fr" ? "fr" : "en";
  const tr = makeT(lang);

  // ============ INIT ============
  useEffect(() => {
    // Sentinel — React.StrictMode double-invokes mount effects in dev and
    // browsers replay navigation on flaky networks. Both would fire this
    // effect twice, and the second call previously tripped the backend's
    // dedupe branch and bounced the respondent straight to PS surveydone.
    // The backend has a grace-window idempotent-init as belt (returns the
    // same interview on rapid replay) — this sentinel is the braces so
    // we don't fire a redundant XHR in the first place.
    if (initFiredRef.current) return;
    initFiredRef.current = true;
    (async () => {
      try {
        const { data } = await api.post("/session/init", {
          campaign_id: campaignId, source,
          participant_id: participantId, assignment_id: assignmentId, project_id: projectId,
          pid,
        });
        if (data.status === "filled") { setPhase("filled"); return; }
        if (data.status === "over_quota") {
          if (data.redirect_url) { window.location.href = data.redirect_url; return; }
          setPhase("filled"); return;
        }
        if (data.status === "unknown_campaign") {
          // PS routed the respondent to a campaign_id we don't have. Bounce
          // them straight back to PureSpectrum with terminate status so
          // they aren't stranded on our error page.
          if (data.redirect_url) { window.location.href = data.redirect_url; return; }
          setPhase("filled"); return;
        }
        setCampaign(data.campaign);
        setInterviewId(data.interview_id);
        setHideReward(data.hide_reward);
        setPhase("consent");
      } catch (e) {
        const detail = e.response?.data?.detail;
        if (e.response?.status === 409 && detail && typeof detail === "object" && detail.code === "already_participated") {
          // If CR provided a redirect, bounce the worker back to Connect so they aren't stuck.
          if (detail.redirect_url) { window.location.href = detail.redirect_url; return; }
          setError(detail.message);
          setPhase("already_participated");
          return;
        }
        // PS-source unrecoverable init failure — bounce respondent back
        // to PS's `/surveydone` with terminate status (18) so they never
        // end up stuck on our loading spinner. This is the last-mile
        // safety net for cases where PS's URL didn't carry enough
        // identifiers, the campaign is in a non-active state (409), or
        // any other backend rejection. Without this, a 4xx here would
        // leave `phase="init"` forever and the respondent would see
        // only a spinner + a browser console error.
        if (source === "cloudresearch" && assignmentId) {
          try {
            const { data: bounce } = await api.get(
              `/session/ps-terminate-url?transaction_id=${encodeURIComponent(assignmentId)}`
            );
            if (bounce?.redirect_url) { window.location.href = bounce.redirect_url; return; }
          } catch (_) { /* fall through to error phase */ }
        }
        setError((detail && typeof detail === "object" ? detail.message : detail) || e.message);
        setPhase("error");
      }
    })();
  }, [campaignId, source, participantId, assignmentId, projectId, pid]);

  // ============ Anti-fraud: blur flag ============
  useEffect(() => {
    if (phase !== "qual" && phase !== "mc") return;
    const onBlur = () => {
      if (!interviewId) return;
      const fd = new FormData();
      fd.append("interview_id", interviewId);
      api.post("/session/flag-blur", fd, { headers: { "Content-Type": "multipart/form-data" } }).catch(() => {});
    };
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, [phase, interviewId]);

  // ============ Audio (+ optional Video) Pre-flight ============
  // For video-enabled campaigns we ENFORCE a strict 1280x720 user-facing
  // (front) camera. If the device can't supply that resolution we redirect
  // the respondent off-flow rather than silently downgrading to text.
  const failHardware = () => {
    const qs = new URLSearchParams({ reason: "hardware_required" });
    if (source) qs.set("source", source);
    if (participantId) qs.set("participant_id", participantId);
    if (assignmentId) qs.set("assignment_id", assignmentId);
    if (projectId) qs.set("project_id", projectId);
    if (interviewId) qs.set("interview_id", interviewId);
    nav(`/micro-research/exit?${qs.toString()}`, { replace: true });
  };

  const requestMic = async () => {
    const modality = campaign?.modality || (campaign?.video_enabled ? "video" : "audio");
    // Text-only campaigns bypass mic + cam preflight entirely.
    if (modality === "text") {
      setHasMic(false);
      proceedToMc();
      return;
    }
    const wantVideo = modality === "video";
    try {
      const constraints = wantVideo
        ? {
            audio: true,
            video: {
              width: { exact: 1280 },
              height: { exact: 720 },
              facingMode: { exact: "user" },
              frameRate: { ideal: 24, max: 30 },
            },
          }
        : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Defense-in-depth: even if getUserMedia returned, double-check the
      // delivered track is actually 1280x720 from a front camera. Some
      // browsers ignore `exact` on Linux/Android and best-effort downgrade.
      if (wantVideo) {
        const vt = stream.getVideoTracks()[0];
        const settings = vt ? vt.getSettings() : {};
        const w = settings.width || 0;
        const h = settings.height || 0;
        const facing = settings.facingMode;
        const okFacing = !facing || facing === "user";
        if (w !== 1280 || h !== 720 || !okFacing) {
          stream.getTracks().forEach((t) => t.stop());
          failHardware();
          return;
        }
      }

      streamRef.current = stream;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const sourceNode = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      sourceNode.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      if (wantVideo && videoPreviewRef.current) {
        try { videoPreviewRef.current.srcObject = stream; }
        catch { /* preview is a UX nicety, not required for capture */ }
      }
      setHasMic(true);
      proceedToMc();
    } catch (err) {
      const modality = campaign?.modality || (campaign?.video_enabled ? "video" : "audio");
      const wantVideoErr = modality === "video";
      if (wantVideoErr) {
        const name = err?.name || "";
        if (name === "OverconstrainedError" || name === "NotFoundError" || name === "NotReadableError") {
          failHardware();
          return;
        }
      }
      // For audio modality we hard-fail when the mic is unavailable; for text
      // there's nothing to fail (handled at top of requestMic).
      if (modality === "audio") {
        const name = err?.name || "";
        if (name === "NotAllowedError" || name === "NotFoundError" || name === "NotReadableError") {
          failHardware();
          return;
        }
      }
      setHasMic(false);
      proceedToMc();
    }
  };

  const proceedToMc = () => {
    if ((campaign?.multiple_choice_questions || []).length > 0) setPhase("mc");
    else startQual();
  };

  // ============ Screener ============
  const screenerQuestions = campaign?.screener_questions || [];
  const modalityOf = (c) => (c?.modality || (c?.video_enabled ? "video" : "audio"));

  const submitScreener = async () => {
    if (Object.keys(screenerAnswers).length !== screenerQuestions.length) {
      setError(tr("error_generic"));
      return;
    }
    try {
      const answers = screenerQuestions.map((_, i) => screenerAnswers[i]);
      const { data } = await api.post("/session/screener", { interview_id: interviewId, answers });
      if (!data.qualified) {
        if (data.redirect_url) { window.location.href = data.redirect_url; return; }
        setPhase("screened_out"); return;
      }
      if (modalityOf(campaign) === "text") { proceedToMc(); return; }
      setPhase("preflight");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const advanceFromConsent = () => setPhase("demographics");

  const advanceAfterDemographics = () => {
    if (screenerQuestions.length > 0) setPhase("screener");
    else if (modalityOf(campaign) === "text") proceedToMc();
    else setPhase("preflight");
  };

  // ============ Demographics (Wave 12) ============
  // Four canonical fields collected on every campaign so the Executive
  // Report has a stable demographic snapshot + persona crosstab base.
  // Kept short — 4 chip-selectors on one screen. Any respondent who
  // picks "prefer not to say" is still routed forward (report renders
  // that bucket as a first-class row so we don't drop the tail).
  const [demoAge, setDemoAge] = useState(null);
  const [demoGender, setDemoGender] = useState(null);
  const [demoProvince, setDemoProvince] = useState(null);
  const [demoProvider, setDemoProvider] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);
  const demoReady = demoAge && demoGender && demoProvince && demoProvider;
  const submitDemographics = async () => {
    if (!demoReady) return;
    setDemoBusy(true);
    try {
      await api.post("/session/demographics", {
        interview_id: interviewId,
        age_bracket: demoAge,
        gender: demoGender,
        province: demoProvince,
        provider_status: demoProvider,
      });
      advanceAfterDemographics();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setDemoBusy(false);
    }
  };

  const startQual = () => {
    const firstQ = campaign?.qualitative_questions?.[0];
    if (firstQ) setMessages([{ role: "ai", text: firstQ }]);
    setPhase("qual");
  };

  // ============ MC ============
  const mcQuestions = campaign?.multiple_choice_questions || [];
  const currentMc = mcQuestions[mcIndex];
  const submitMc = async (option) => {
    const next = { ...mcAnswers, [currentMc.question_text]: option };
    setMcAnswers(next);
    if (mcIndex + 1 < mcQuestions.length) {
      setMcIndex(mcIndex + 1);
    } else {
      try {
        await api.post("/session/multiple-choice", {
          interview_id: interviewId, responses: next,
        });
      } catch { /* non-fatal */ }
      startQual();
    }
  };

  // ============ Qual recording lifecycle ============
  const startRecording = async () => {
    if (!streamRef.current) return;
    try {
      const wantVideo = !!campaign?.video_enabled;
      let mimeType;
      if (wantVideo) {
        mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
          ? "video/webm;codecs=vp8,opus"
          : (MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
              ? "video/webm;codecs=vp9,opus"
              : (MediaRecorder.isTypeSupported("video/mp4") ? "video/mp4" : "video/webm"));
      } else {
        mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "audio/webm");
      }
      const recorderOpts = wantVideo
        ? { mimeType, audioBitsPerSecond: 64000, videoBitsPerSecond: 1_200_000 }
        : { mimeType, audioBitsPerSecond: 32000 };
      const mr = new MediaRecorder(streamRef.current, recorderOpts);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = onRecordingStopped;
      mr.start(250);
      mediaRecRef.current = mr;
      setRecording(true);
    } catch {
      setError("We couldn't access your microphone. Please check browser permissions and refresh.");
    }
  };

  const stopRecording = () => {
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
      mediaRecRef.current.stop();
    }
    setRecording(false);
  };

  const onRecordingStopped = async () => {
    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
    if (blob.size < 1500) return;
    setSending(true);
    try { await saveChunkLocal(blob, { interviewId, qIndex }); } catch { /* best-effort */ }
    const isVideo = (blob.type || "").startsWith("video/");
    const uploadName = isVideo
      ? ((blob.type || "").includes("mp4") ? "interview.mp4" : "interview.webm")
      : "audio.webm";
    try {
      const fd = new FormData();
      fd.append("interview_id", interviewId);
      fd.append("q_index", String(qIndex));
      fd.append("audio", blob, uploadName);
      const { data } = await api.post("/session/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const text = data.text || "";
      await sendUserMessage(text);
      if (data.audio_path) {
        api.post("/session/attach-audio-meta", {
          interview_id: interviewId,
          q_index: qIndex,
          text,
          audio_path: data.audio_path,
          duration_ms: data.duration_ms || 0,
          segments: data.segments || [],
          prosody: data.prosody || null,
        }).catch(() => { /* non-fatal */ });
      }
    } catch {
      setError(tr("qual_transcribing") === "Transcribing…" ? "We couldn't transcribe that. Please try again or type your answer." : "Nous n'avons pas pu transcrire. Veuillez réessayer ou taper votre réponse.");
      setSending(false);
    }
  };

  const sendUserMessage = async (text) => {
    if (!text.trim()) return;
    setSending(true);
    setMessages((m) => [...m, { role: "user", text }]);
    try {
      const { data } = await api.post("/session/chat-turn", {
        interview_id: interviewId,
        main_question_index: qIndex,
        role: "user",
        text,
      });
      if (data.next_action === "terminate") {
        setMessages((m) => [...m, { role: "ai", text: data.ai_reply }]);
        setPhase("terminated");
        return;
      }
      if (data.next_action === "retry") {
        setMessages((m) => [...m, { role: "ai", text: data.ai_reply }]);
        setSending(false);
        setDraft("");
        return;
      }
      if (data.ai_reply) {
        setMessages((m) => [...m, { role: "ai", text: data.ai_reply }]);
      }
      if (data.next_action === "next_question") {
        setQIndex(data.main_question_index);
      } else if (data.next_action === "finish") {
        await finalize();
        return;
      }
      setDraft("");
    } catch {
      setError("Send failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const finalize = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/session/finalize", { interview_id: interviewId });
      setFinalScreen(data);
      if (data.status === "flagged") setPhase("review");
      else setPhase("finished");
      // PS reconciliation requires the respondent's browser to actually
      // hit `spectrumsurveys.com/surveydone?st=21&transaction_id=<aid>`.
      // Give the "Returning you to the panel…" screen ~900ms of dwell so
      // the respondent sees confirmation, then fire the redirect. Without
      // this, PS never marks the transaction complete on their side even
      // though our interview is verified.
      if (data.redirect_url) {
        setTimeout(() => {
          window.location.href = data.redirect_url;
        }, 900);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    setError("For research integrity, pasting is disabled. Please type or use the microphone.");
  };

  // ============ RENDER ============
  // Error branch first (was previously unreachable because phase==="init"
  // short-circuited the spinner render above). PS-source failures never
  // reach here — they're bounced back to /surveydone in the init catch.
  if (phase === "error" || (error && phase === "init")) {
    return (
      <Centered>
        <div className="max-w-md text-center space-y-2" data-testid="session-error">
          <div className="text-[var(--rl-accent)] font-semibold">Something went wrong</div>
          <div className="text-sm text-[var(--rl-text-secondary)]">{String(error || "We couldn't start this session. Please try again later.")}</div>
        </div>
      </Centered>
    );
  }
  if (phase === "init") return <Centered><Loader2 className="w-6 h-6 animate-spin" /></Centered>;

  if (phase === "consent") {
    return (
      <ScreenConsent
        campaign={campaign} tr={tr}
        onContinue={advanceFromConsent}
      />
    );
  }

  if (phase === "demographics") {
    // Chip styling helper — inlined via className rather than a
    // sub-component so React doesn't re-mount every chip on each state
    // change (react/no-unstable-nested-components).
    const chipCls = (isActive) =>
      "px-3 py-2 text-sm border transition-colors " +
      (isActive
        ? "bg-[var(--rl-text)] text-white border-[var(--rl-text)]"
        : "bg-white border-[var(--rl-border)] hover:border-[var(--rl-text)]");
    return (
      <Centered>
        <div className="max-w-2xl w-full space-y-6" data-testid="demographics-screen">
          <div>
            <h1 className="font-display font-black text-3xl">A few quick questions</h1>
            <p className="text-sm text-[var(--rl-text-secondary)] mt-2">
              These help us make sense of the research group as a whole. Your individual answers stay anonymous.
            </p>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--rl-text-secondary)] mb-2">Age</div>
            <div className="flex flex-wrap gap-2" data-testid="demo-age-group">
              {["18-24","25-34","35-44","45-54","55-64","65+","prefer_not_to_say"].map((v) => (
                <button key={v} type="button" onClick={() => setDemoAge(v)} className={chipCls(demoAge === v)} data-testid={`demo-age-${v}`}>
                  {v === "prefer_not_to_say" ? "Prefer not to say" : v}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--rl-text-secondary)] mb-2">Gender</div>
            <div className="flex flex-wrap gap-2" data-testid="demo-gender-group">
              {[["female","Female"],["male","Male"],["nonbinary","Non-binary"],["prefer_not_to_say","Prefer not to say"]].map(([v,l]) => (
                <button key={v} type="button" onClick={() => setDemoGender(v)} className={chipCls(demoGender === v)} data-testid={`demo-gender-${v}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--rl-text-secondary)] mb-2">Province / region</div>
            <div className="flex flex-wrap gap-2" data-testid="demo-province-group">
              {[["ON","Ontario"],["QC","Quebec"],["BC","British Columbia"],["AB","Alberta"],["MB","Manitoba"],["SK","Saskatchewan"],["NS","Nova Scotia"],["NB","New Brunswick"],["NL","Newfoundland & Labrador"],["PE","Prince Edward Island"],["YT","Yukon"],["NT","Northwest Territories"],["NU","Nunavut"],["outside_ca","Outside Canada"],["prefer_not_to_say","Prefer not to say"]].map(([v,l]) => (
                <button key={v} type="button" onClick={() => setDemoProvince(v)} className={chipCls(demoProvince === v)} data-testid={`demo-province-${v}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--rl-text-secondary)] mb-2">Do you have a regular family doctor (GP)?</div>
            <div className="flex flex-wrap gap-2" data-testid="demo-provider-group">
              {[["attached","Yes — I have a family doctor"],["unattached","No — I use clinics / walk-ins / ER"],["not_sure","Not sure"],["prefer_not_to_say","Prefer not to say"]].map(([v,l]) => (
                <button key={v} type="button" onClick={() => setDemoProvider(v)} className={chipCls(demoProvider === v)} data-testid={`demo-provider-${v}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="pt-2">
            <button
              type="button"
              data-testid="demographics-continue-btn"
              onClick={submitDemographics}
              disabled={!demoReady || demoBusy}
              className="w-full bg-[var(--rl-text)] text-white py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-50"
            >
              {demoBusy ? "Saving…" : "Continue"}
            </button>
          </div>
        </div>
      </Centered>
    );
  }

  if (phase === "screener") {
    return (
      <ScreenScreener
        screenerQuestions={screenerQuestions}
        screenerAnswers={screenerAnswers} setScreenerAnswers={setScreenerAnswers}
        tr={tr} onSubmit={submitScreener}
      />
    );
  }

  if (phase === "preflight") {
    return (
      <ScreenPreflight
        campaign={campaign}
        onAllow={requestMic}
        onSkip={() => { setHasMic(false); proceedToMc(); }}
      />
    );
  }

  if (phase === "mc") {
    return (
      <ScreenMc
        mcIndex={mcIndex} mcQuestions={mcQuestions} currentMc={currentMc}
        onPick={submitMc}
      />
    );
  }

  if (phase === "qual") {
    return (
      <ScreenQual
        campaign={campaign} qIndex={qIndex}
        messages={messages} sending={sending} error={error}
        recording={recording} hasMic={hasMic}
        draft={draft} setDraft={setDraft}
        startRecording={startRecording} stopRecording={stopRecording}
        sendUserMessage={sendUserMessage} handlePaste={handlePaste}
        analyserRef={analyserRef} videoPreviewRef={videoPreviewRef}
      />
    );
  }

  // All terminal screens: filled, already_participated, screened_out,
  // terminated, review, finished.
  return (
    <ScreenFinal
      phase={phase} campaign={campaign} finalScreen={finalScreen}
      hideReward={hideReward} lang={lang} tr={tr} nav={nav}
    />
  );
}
